/* eslint-disable no-use-before-define */
/* pre */

import React, { lazy, useEffect } from "react";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";

import { useRoleAuthorization } from "../../hooks/useRoleAuthorization";
import { useUserState } from "../../context/UserContext";
import { useHttpRequest } from "../../hooks/useHttpRequest";

// styles
import useStyles from "./styles";

// components
import Header from "../Header";
import Sidebar from "../Sidebar";
import Rightbar from "../Rightbar/Rightbar";

// pages
const AccountChangePassword = lazy(() => import("../../pages/account/ChangePassword"));
const MaterialRequestList = lazy(() => import("../../pages/material-request/MaterialRequest"));
const MaterialRequestNew = lazy(() => import("../../pages/material-request/NewOrder"));
const MaterialRequestCard = lazy(() => import("../../pages/material-request/OrderView"));
const MaterialList = lazy(() => import("../../pages/material/ManageMaterial"));
const MaterialCard = lazy(() => import("../../pages/material/MaterialCard"));
const ProjectList = lazy(() => import("../../pages/project/ManageProjects"));
const ProjectCard = lazy(() => import("../../pages/project/ProjectCard"));
const UserList = lazy(() => import("../../pages/user/ManageUsers"));
const UserCard = lazy(() => import("../../pages/user/UserCard"));
const ServiceClaimList = lazy(() => import("../../pages/service-claim/ManageServiceClaims"));
const ServiceClaimCard = lazy(() => import("../../pages/service-claim/ServiceClaimCard"));
const ServiceClaimEdit = lazy(() => import("../../pages/service-claim/ServiceClaimEdit"));
const BracketList = lazy(() => import("../../pages/bracket/BracketList"));
const BracketCard = lazy(() => import("../../pages/bracket/BracketCard"));
const BracketQualityCheck = lazy(() => import("../../pages/bracket/BracketQualityCheck"));
const BracketRequestList = lazy(() => import("../../pages/bracket-request/BracketRequestList"));
const BracketRequestCard = lazy(() => import("../../pages/bracket-request/BracketRequestCard"));
const CustomerExistingAircon = lazy(() => import("../../pages/customer-existing-aircon/CustomerExistingAircon"));
const CustomerExistingAirconCard = lazy(() => import("../../pages/customer-existing-aircon/CustomerExistingAirconCard"));
const BracketBatchList = lazy(() => import("../../pages/bracket-batch/BracketBatchList"));
const BracketBatchCard = lazy(() => import("../../pages/bracket-batch/BracketBatchCard"));
const ServiceClaimMatrixList = lazy(() => import("../../pages/service-claim-matrix/ServiceClaimMatrixList"));
const ServiceClaimMatrixCard = lazy(() => import("../../pages/service-claim-matrix/ServiceClaimMatrixCard"));
const TwoFASetup = lazy(() => import("../../pages/login/TwoFASetup"));
const TwoFAVerification = lazy(() => import("../../pages/login/TwoFAVerification"));
const TaskOverview = lazy(() => import("../../pages/task/TaskOverview"));
const TaskCard = lazy(() => import("../../pages/task/TaskCard"));
const StaffPlanningList = lazy(() => import ( "../../pages/staff-planning/StaffPlanningList"));
const TaskTrackingList = lazy(() => import("../../pages/task-tracking/TaskTrackingList"));
const TaskTrackingCard = lazy(() => import("../../pages/task-tracking/TaskTrackingCard"));
const TaskspaceList = lazy(() => import("../../pages/taskspace/TaskspaceList"));
const RoleList = lazy(() => import("../../pages/role/RoleList"));
const RoleCard = lazy(() => import("../../pages/role/RoleCard"));
const ProjectRequestList = lazy(() => import("../../pages/project-request/ProjectRequestList"));
const ProjectRequestCard = lazy(() => import("../../pages/project-request/ProjectRequestCard"));
const PayServiceClaim = lazy(() => import("../../pages/pay-service-claim/PayServiceClaim"));
const ServiceClaimReportList = lazy(() => import("../../pages/service-claim-report/ServiceClaimReportList"));
const ServiceClaimReportCard = lazy(() => import("../../pages/service-claim-report/ServiceClaimReportCard"));
const InstallationClaimList = lazy(() => import("../../pages/installation-claim/ManageInstallationClaims"));
const InstallationClaimCard = lazy(() => import("../../pages/installation-claim/InstallationClaimCard"));
const InstallationClaimEdit = lazy(() => import("../../pages/installation-claim/InstallationClaimEdit"));
const InstallationClaimMatrixList = lazy(() => import("../../pages/installation-claim-matrix/InstallationClaimMatrixList"));
const InstallationClaimMatrixCard = lazy(() => import("../../pages/installation-claim-matrix/InstallationClaimMatrixCard"));
const InstallationClaimReportList = lazy(() => import("../../pages/installation-claim-report/InstallationClaimReportList"));
const InstallationClaimReportCard = lazy(() => import("../../pages/installation-claim-report/InstallationClaimReportCard"));
const FnbEventBookingList = lazy(() => import("../../pages/fnb-event-booking/FnbEventBookingList"));
const FnbEventBookingCard = lazy(() => import("../../pages/fnb-event-booking/FnbEventBookingCard"));
const FnbEventMenuList = lazy(() => import("../../pages/fnb-event-menu/FnbEventMenuList"));
const FnbEventMenuCard = lazy(() => import("../../pages/fnb-event-menu/FnbEventMenuCard"));
const FnbEventBookingCalendar = lazy(() => import("../../pages/fnb-event-booking/FnbEventBookingCalendar"));
const FnbFoodList = lazy(() => import("../../pages/fnb-food/FnbFoodList"));
const FnbFoodCard = lazy(() => import("../../pages/fnb-food/FnbFoodCard"));
const FnbStoreList = lazy(() => import("../../pages/fnb-store/FnbStoreList"));
const FnbStoreCard = lazy(() => import("../../pages/fnb-store/FnbStoreCard"));
const FnbKitckenDashboardRedirect = lazy(() => import("../../pages/fnb-kitcken-dashboard-redirect/FnbKitckenDashboardRedirect"));
const FnbOrderManagement = lazy(() => import("../../pages/fnb-order/FnbOrderManagement"));
const FnbOrderCard = lazy(() => import("../../pages/fnb-order/FnbOrderCard"));
const FnbStoreEventList = lazy(() => import("../../pages/fnb-store-event/FnbStoreEventList"));
const FnbStoreEventCard = lazy(() => import("../../pages/fnb-store-event/FnbStoreEventCard"));
const FnbReport = lazy(() => import("../../pages/fnb-report/FnbReport"));
const MachineCompanyList = lazy(() => import("../../pages/machine-company/MachineCompanyList"));
const MachineCompanyCard = lazy(() => import("../../pages/machine-company/MachineCompanyCard"));
const MachineStoreList = lazy(() => import("../../pages/machine-store/MachineStoreList"));
const MachineStoreCard = lazy(() => import("../../pages/machine-store/MachineStoreCard"));
const MachineServiceJobList = lazy(() => import("../../pages/machine-service-job/MachineServiceJobList"));
const MachineServiceJobCard = lazy(() => import("../../pages/machine-service-job/MachineServiceJobCard"));
const GBBookingList = lazy(() => import("../../pages/group-buy-booking/BookingList"));
const GBBookingCard = lazy(() => import("../../pages/group-buy-booking/BookingCard"));
const GBCalendarList = lazy(() => import("../../pages/group-buy-calendar/CalendarList"));
const GBCalendarCard = lazy(() => import("../../pages/group-buy-calendar/CalendarCard"));
const GroupBuyAttendanceDashboard = lazy(() => import("../../pages/group-buy-attendance/AttendanceDashboard"));
const GroupBuyAttendanceEvent = lazy(() => import("../../pages/group-buy-attendance/AttendanceEvent"));
const GBAttribute = lazy(() => import("../../pages/group-buy-attribute/Attribute"));
const AirconSubscriptionAttribute = lazy(() => import("../../pages/aircon-subscription-attribute/Attribute"));
const CompanyList = lazy(() => import("../../pages/company/CompanyList"));
const CompanyCard = lazy(() => import("../../pages/company/company-card"));
const FnbPresetList = lazy(() => import("../../pages/fnb-preset/FnbPresetList"));
const ActivityLogList = lazy(() => import("../../pages/activity-log/ActivityLogList"));
const FnbVoucherSetupList = lazy(() => import("../../pages/fnb-voucher-setup/FnbVoucherSetupList"));
const FnbVoucherSetupCard = lazy(() => import("../../pages/fnb-voucher-setup/FnbVoucherSetupCard"));
const AirconSubscriptionMatrixList = lazy(() => import("../../pages/aircon-subscription-matrix/AirconSubscriptionMatrixList"));
const AirconSubscriptionMatrixCard = lazy(() => import("../../pages/aircon-subscription-matrix/AirconSubscriptionMatrixCard"));
const AirconSubscriptionList = lazy(() => import("../../pages/aircon-subscription/AirconSubscriptionList"));
const CarWashRequestList = lazy(() => import("../../pages/car-wash-request/CarWashRequestList"));
const CarWashRequestCard = lazy(() => import("../../pages/car-wash-request/CarWashRequestCard"));
const CarWashSetup = lazy(() => import("../../pages/car-wash-setup/Setup"));
const FnbSupplierOrderCard = lazy(() => import("../../pages/fnb-supplier/FnbSupplierOrderCard"));
const FnbSupplierOrderList = lazy(() => import("../../pages/fnb-supplier/FnbSupplierOrderList"));
const FnbSupplierList = lazy(() => import("../../pages/fnb-supplier/SupplierList"));
const SupplierListCard = lazy(() => import("../../pages/fnb-supplier/SupplierListCard"));
const PMOChargeCard = lazy(() => import("../../pages/pmo-charge/pmo-charge-card"));
const PMOChargeList = lazy(() => import("../../pages/pmo-charge/pmo-charge-list"));
const PMOChargeCalendarView = lazy(() => import("../../pages/pmo-charge/pmo-charge-calendar-view"));
const SPLeaveCard = lazy(() => import("../../pages/sp-leave/sp-leave-card"));
const SPLeaveList = lazy(() => import("../../pages/sp-leave/sp-leave-list"));
const MaterialReturnCard = lazy(() => import("../../pages/material-return/material-return-card"));
const MaterialReturnList = lazy(() => import("../../pages/material-return/material-return-list"));
const LoyaltyVoucherProfilePage = lazy(() => import("../../pages/loyalty-voucher/LoyaltyVoucherProfile"));
const LoyaltyVoucherProfileListPage = lazy(() => import("../../pages/loyalty-voucher/LoyaltyVoucherProfileListPage"));
const PMOChargeWorkerList = lazy(() => import("../../pages/pmo-charge-worker/pmo-charge-worker-list"));
const PMOChargeWorkerCard = lazy(() => import("../../pages/pmo-charge-worker/pmo-charge-worker-card"));
const LoanEquipmentCard = lazy(() => import("../../pages/loan-equipment/loan-equipment-card"));
const LoanEquipmentList = lazy(() => import("../../pages/loan-equipment/loan-equipment-list"));
const EquipmentList = lazy(() => import("../../pages/equipment/equipment-list"));
const EquipmentCard = lazy(() => import("../../pages/equipment/equipment-card"));
const CustomerVoucherListPage = lazy(() => import("../../pages/loyalty-voucher/CustomerVoucherListPage"));
const PMOChargeWorkerImportRate = lazy(() => import("../../pages/pmo-charge-worker/pmo-charge-worker-import-rate"));
const PMOChargeWorkerConfigListPage = lazy(() => import("../../pages/pmo-charge-worker/pmo-charge-worker-config-list"));
const PMOChargeWorkerConfigCardPage = lazy(() => import("../../pages/pmo-charge-worker/pmo-charge-worker-config-card"));
const PermitToWorkList = lazy(() => import("../../pages/permit-to-work/permit-to-work-list"));
const PermitToWorkCard = lazy(() => import("../../pages/permit-to-work/permit-to-work-card"));
const FnbDCSRedemption = lazy(() => import('../../pages/fnb-dcs-membership/FnbDcsRedemption'));
const FnbDCSMembershipList = lazy(() => import('../../pages/fnb-dcs-membership/FnbDcsMembershipList'));
const FnbDCSMembershipImport = lazy(() => import('../../pages/fnb-dcs-membership/FnbDcsMembershipImport'));
const FnbDcsMenuSetupList = lazy(() => import('../../pages/fnb-dcs-menu-setup/FnbDcsMenuSetupList'));
const FnbDcsMenuSetupCard = lazy(() => import('../../pages/fnb-dcs-menu-setup/FnbDcsMenuSetupCard'));

function Layout(props) {
  var classes = useStyles();
  const { userInfo } = useUserState();
  const { auth } = useRoleAuthorization();

  const { sendRequest: getUserInfoAPI } = useHttpRequest();

  useEffect(() => {
    getUserInfoAPI("/v1/user/check-two-fa-status-for-redirect");
  }, []);

  const navigateByUser = () => {
    if (userInfo?.defaultUrl) {
      if (userInfo?.defaultUrl != "") {
        return <Redirect to={userInfo?.defaultUrl} />;
      } else {
        return <Redirect to='/app/dashboard' />;
      }
    }
  };

  return (
    <div className={classes.root}>
      <>
        <Header history={props.history} />
        <Sidebar />
        <div className={"main"}>
          {/* <div className={classes.fakeToolbar} /> */}
          <Switch>
            <Route exact path='/app/account' render={() => navigateByUser()} />
            <Route exact path='/app/account/change-password' component={AccountChangePassword} />

            <Route exact path='/app/material-request' render={() => <Redirect to='/app/material-request/list' />} />
            <Route exact path='/app/material-request/list' component={MaterialRequestList} />
            <Route exact path='/app/material-request/new' component={MaterialRequestNew} />
            <Route exact path='/app/material-request/card/:orderCode' component={MaterialRequestCard} />

            <Route exact path='/app/material' render={() => <Redirect to='/app/material/list' />} />
            <Route exact path='/app/material/list' component={MaterialList} />
            <Route exact path='/app/material/new' component={MaterialCard} />
            <Route exact path='/app/material/card/:itemId' component={MaterialCard} />

            <Route exact path='/app/project' render={() => <Redirect to='/app/project/list' />} />
            <Route exact path='/app/project/list' component={ProjectList} />
            <Route exact path='/app/project/card' component={ProjectCard} />
            <Route exact path='/app/project/card/:projectId' component={ProjectCard} />

            <Route exact path='/app/user' render={() => <Redirect to='/app/user/list' />} />
            <Route exact path='/app/user/list' component={UserList} />
            <Route exact path='/app/user/card' component={UserCard} />
            <Route exact path='/app/user/card/:userId' component={UserCard} />

            <Route exact path='/app/service-claim' render={() => <Redirect to='/app/service-claim/list' />} />
            <Route exact path='/app/service-claim/list' component={ServiceClaimList} />
            <Route exact path='/app/service-claim/card' component={ServiceClaimCard} />
            <Route exact path='/app/service-claim/edit/:invoiceNo' component={ServiceClaimEdit} />
            <Route exact path='/app/service-claim/pay' component={PayServiceClaim} />

            <Route exact path='/app/brackets' render={() => <Redirect to='/app/brackets/list' />} />
            <Route exact path='/app/bracket/list' component={BracketList} />
            <Route exact path='/app/bracket/card' component={BracketCard} />
            <Route exact path='/app/bracket/card/:serialNo' component={BracketCard} />
            <Route exact path='/app/bracket/quality-check' component={BracketQualityCheck} />

            <Route exact path='/app/bracket-request' render={() => <Redirect to='/app/bracket-request/list' />} />
            <Route exact path='/app/bracket-request/list' component={BracketRequestList} />
            <Route exact path='/app/bracket-request/card' component={BracketRequestCard} />
            <Route exact path='/app/bracket-request/card/:pkrNo' component={BracketRequestCard} />

            <Route exact path='/app/group-buy-booking' render={() => <Redirect to='/app/group-buy-booking/list' />} />
            <Route exact path='/app/group-buy-booking/list' component={GBBookingList} />
            <Route exact path='/app/group-buy-booking/card' component={GBBookingCard} />
            <Route exact path='/app/group-buy-booking/card/:id' component={GBBookingCard} />

            <Route exact path='/app/group-buy-calendar' render={() => <Redirect to='/app/group-buy-calendar/list' />} />
            <Route exact path='/app/group-buy-calendar/list' component={GBCalendarList} />
            <Route exact path='/app/group-buy-calendar/card' component={GBCalendarCard} />
            <Route exact path='/app/group-buy-calendar/card/:id' component={GBCalendarCard} />

            <Route exact path='/app/group-buy-attendance' component={GroupBuyAttendanceDashboard} />
            <Route exact path='/app/group-buy-attendance/:id' component={GroupBuyAttendanceEvent} />

            <Route exact path='/app/group-buy-attribute' component={GBAttribute} />

            <Route exact path='/app/customer-existing-aircon'
                   render={() => <Redirect to='/app/customer-existing-aircon/list' />} />
            <Route exact path='/app/customer-existing-aircon/list' component={CustomerExistingAircon} />
            <Route exact path='/app/customer-existing-aircon/card' component={CustomerExistingAirconCard} />
            <Route exact path='/app/customer-existing-aircon/card/:salesOrderNo'
                   component={CustomerExistingAirconCard} />

            <Route exact path='/app/bracket-batch' render={() => <Redirect to='/app/bracket-batch/list' />} />
            <Route exact path='/app/bracket-batch/list' component={BracketBatchList} />
            <Route exact path='/app/bracket-batch/card' component={BracketBatchCard} />

            <Route exact path='/app/service-claim-matrix'
                   render={() => <Redirect to='/app/service-claim-matrix/list' />} />
            <Route exact path='/app/service-claim-matrix/list' component={ServiceClaimMatrixList} />
            <Route exact path='/app/service-claim-matrix/card' component={ServiceClaimMatrixCard} />
            <Route exact path='/app/service-claim-matrix/card/:matrixId' component={ServiceClaimMatrixCard} />

            <Route exact path='/app/two-fa-setup' component={TwoFASetup} />
            <Route exact path='/app/two-fa-verification' component={TwoFAVerification} />

            <Route exact path='/app/task' render={() => <Redirect to='/app/task/list' />} />
            <Route exact path='/app/task/list' component={TaskOverview} />
            <Route exact path='/app/task/card' component={TaskCard} />
            <Route exact path='/app/task/card/:taskId' component={TaskCard} />
            <Route exact path='/app/staff-planning' render={() => <Redirect to='/app/staff-planning/list' />} />
            <Route exact path='/app/staff-planning/list' component={StaffPlanningList} />
            <Route exact path='/app/taskspace' render={() => <Redirect to='/app/taskspace/list' />} />
            <Route exact path='/app/taskspace/list' component={TaskspaceList} />

            <Route exact path='/app/task-tracking' render={() => <Redirect to='/app/task-tracking/list' />} />
            <Route exact path='/app/task-tracking/list' component={TaskTrackingList} />
            <Route exact path='/app/task-tracking/card/:staffId/:date/:slot' component={TaskTrackingCard} />

            <Route exact path='/app/role' render={() => <Redirect to='/app/role/list' />} />
            <Route exact path='/app/role/list' component={RoleList} />
            <Route exact path='/app/role/card' component={RoleCard} />
            <Route exact path='/app/role/card/:roleId' component={RoleCard} />

            <Route exact path='/app/project-request' render={() => <Redirect to='/app/project-request/list' />} />
            <Route exact path='/app/project-request/list' component={ProjectRequestList} />
            <Route exact path='/app/project-request/card' component={ProjectRequestCard} />
            <Route exact path='/app/project-request/card/:requestId' component={ProjectRequestCard} />

            <Route exact path='/app/service-claim-report'
                   render={() => <Redirect to='/app/service-claim-report/list' />} />
            <Route exact path='/app/service-claim-report/list' component={ServiceClaimReportList} />
            <Route exact path='/app/service-claim-report/card/:batchNo' component={ServiceClaimReportCard} />

            <Route exact path='/app/installation-claim' render={() => <Redirect to='/app/installation-claim/list' />} />
            <Route exact path='/app/installation-claim/list' component={InstallationClaimList} />
            <Route exact path='/app/installation-claim/card' component={InstallationClaimCard} />
            <Route exact path='/app/installation-claim/edit/:invoiceNo/:team' component={InstallationClaimEdit} />

            <Route exact path='/app/installation-claim-matrix'
                   render={() => <Redirect to='/app/installation-claim-matrix/list' />} />
            <Route exact path='/app/installation-claim-matrix/list' component={InstallationClaimMatrixList} />
            <Route exact path='/app/installation-claim-matrix/card' component={InstallationClaimMatrixCard} />
            <Route exact path='/app/installation-claim-matrix/card/:matrixId' component={InstallationClaimMatrixCard} />

            <Route exact path='/app/installation-claim-report'
                   render={() => <Redirect to='/app/installation-claim-report/list' />} />
            <Route exact path='/app/installation-claim-report/list' component={InstallationClaimReportList} />
            <Route exact path='/app/installation-claim-report/card/:batchNo' component={InstallationClaimReportCard} />

            <Route exact path='/app/fnb-event-booking' render={() => <Redirect to='/app/fnb-event-booking/list' />} />
            <Route exact path='/app/fnb-event-booking/list' component={FnbEventBookingList} />
            <Route exact path='/app/fnb-event-booking/card' component={FnbEventBookingCard} />
            <Route exact path='/app/fnb-event-booking/card/:eventBookingId' component={FnbEventBookingCard} />
            <Route exact path='/app/fnb-event-booking/calendar' component={FnbEventBookingCalendar} />

            <Route exact path='/app/fnb-event-menu' render={() => <Redirect to='/app/fnb-event-menu/list' />} />
            <Route exact path='/app/fnb-event-menu/list' component={FnbEventMenuList} />
            <Route exact path='/app/fnb-event-menu/card' component={FnbEventMenuCard} />
            <Route exact path='/app/fnb-event-menu/card/:eventMenuId' component={FnbEventMenuCard} />

            <Route exact path='/app/fnb-food' render={() => <Redirect to='/app/fnb-food/list' />} />
            <Route exact path='/app/fnb-food/list' component={FnbFoodList} />
            <Route exact path='/app/fnb-food/card' component={FnbFoodCard} />
            <Route exact path='/app/fnb-food/card/:foodId' component={FnbFoodCard} />

            <Route exact path='/app/fnb-preset' component={FnbPresetList} />
            <Route exact path='/app/fnb-dcs-redemption' component={FnbDCSRedemption} />
            <Route exact path='/app/fnb-dcs-membership' component={FnbDCSMembershipList} />
            <Route exact path='/app/fnb-dcs-membership/import' component={FnbDCSMembershipImport} />
            <Route exact path='/app/fnb-dcs-menu-setup' component={FnbDcsMenuSetupList} />
            <Route exact path='/app/fnb-dcs-menu-setup/card' component={FnbDcsMenuSetupCard} />
            <Route exact path='/app/fnb-dcs-menu-setup/card/:perkId' component={FnbDcsMenuSetupCard} />

            <Route exact path='/app/fnb-store' render={() => <Redirect to='/app/fnb-store/list' />} />
            <Route exact path='/app/fnb-store/list' component={FnbStoreList} />
            <Route exact path='/app/fnb-store/card' component={FnbStoreCard} />
            <Route exact path='/app/fnb-store/card/:storeId' component={FnbStoreCard} />

            <Route exact path='/app/fnb-kitchen-dashboard'
                   render={() => <Redirect to='/app/fnb-kitchen-dashboard/redirect' />} />
            <Route exact path='/app/fnb-kitchen-dashboard/redirect' component={FnbKitckenDashboardRedirect} />

            <Route exact path='/app/fnb-store-event' render={() => <Redirect to='/app/fnb-store-event/list' />} />
            <Route exact path='/app/fnb-store-event/list' component={FnbStoreEventList} />
            <Route exact path='/app/fnb-store-event/card' component={FnbStoreEventCard} />
            <Route exact path='/app/fnb-store-event/card/:eventId' component={FnbStoreEventCard} />

            <Route exact path='/app/fnb-order/' render={() => <Redirect to='/app/fnb-order/' />} />
            <Route exact path='/app/fnb-order/list' component={FnbOrderManagement} />
            <Route exact path='/app/fnb-order/card/:orderId' component={FnbOrderCard} />

            <Route exact path='/app/fnb-voucher-setup' component={FnbVoucherSetupList} />
            <Route exact path='/app/fnb-voucher-setup/:voucherId' component={FnbVoucherSetupCard} />
            <Route exact path='/app/fnb-voucher-setup/new' component={FnbVoucherSetupCard} />

            {/* Fnb Supplier Order */}
            <Route exact path='/app/fnb-supplier-order/new' component={FnbSupplierOrderCard} />
            <Route exact path='/app/fnb-supplier-order/:orderId' component={FnbSupplierOrderCard} />
            <Route exact path='/app/fnb-supplier-order' component={FnbSupplierOrderList} />

            <Route exact path='/app/fnb-supplier-list' component={FnbSupplierList} />
            <Route exact path='/app/fnb-supplier-list/new' component={SupplierListCard} />
            <Route exact path='/app/fnb-supplier-list/:vendorCode' component={SupplierListCard} />

            <Route exact path='/app/fnb-report' render={() => <Redirect to='/app/fnb-report/report' />} />
            <Route exact path='/app/fnb-report/report' component={FnbReport} />

            <Route exact path='/app/machine-company/' render={() => <Redirect to='/app/machine-company/' />} />
            <Route exact path='/app/machine-company/list' component={MachineCompanyList} />
            <Route exact path='/app/machine-company/card' component={MachineCompanyCard} />
            <Route exact path='/app/machine-company/card/:companyId' component={MachineCompanyCard} />

            <Route exact path='/app/machine-store/' render={() => <Redirect to='/app/machine-store/' />} />
            <Route exact path='/app/machine-store/list' component={MachineStoreList} />
            <Route exact path='/app/machine-store/card' component={MachineStoreCard} />
            <Route exact path='/app/machine-store/card/:storeId' component={MachineStoreCard} />

            <Route exact path='/app/machine-service-job/' render={() => <Redirect to='/app/machine-service-job/' />} />
            <Route exact path='/app/machine-service-job/list' component={MachineServiceJobList} />
            <Route exact path='/app/machine-service-job/card' component={MachineServiceJobCard} />
            <Route exact path='/app/machine-service-job/card/:serviceJobId' component={MachineServiceJobCard} />

            <Route exact path='/app/company' render={() => <Redirect to='/app/company/list' />} />
            <Route exact path='/app/company/list' component={CompanyList} />
            <Route exact path='/app/company/card' component={CompanyCard} />
            <Route exact path='/app/company/card/:id' component={CompanyCard} />

            <Route exact path='/app/activity-log' component={ActivityLogList} />

            <Route exact path='/app/aircon-subscription-attribute' component={AirconSubscriptionAttribute} />

            <Route exact path='/app/aircon-subscription-matrix'
                   render={() => <Redirect to='/app/aircon-subscription-matrix/list' />} />
            <Route exact path='/app/aircon-subscription-matrix/list' component={AirconSubscriptionMatrixList} />
            <Route exact path='/app/aircon-subscription-matrix/card' component={AirconSubscriptionMatrixCard} />
            <Route exact path='/app/aircon-subscription-matrix/card/:id' component={AirconSubscriptionMatrixCard} />

            <Route exact path='/app/aircon-subscription'
                   render={() => <Redirect to='/app/aircon-subscription/list' />} />
            <Route exact path='/app/aircon-subscription/list' component={AirconSubscriptionList} />

            <Route exact path='/app/car-wash-request' render={() => <Redirect to='/app/car-wash-request/list' />} />
            <Route exact path='/app/car-wash-request/list' component={CarWashRequestList} />
            <Route exact path='/app/car-wash-request/card' component={CarWashRequestCard} />
            <Route exact path='/app/car-wash-request/card/:id' component={CarWashRequestCard} />
            <Route exact path='/app/car-wash-setup' component={CarWashSetup} />

            <Route exact path='/app/pmo-charge' render={() => <Redirect to='/app/pmo-charge/list' />} />
            <Route exact path='/app/pmo-charge/list' component={PMOChargeList} />
            <Route exact path='/app/pmo-charge/card' component={PMOChargeCard} />
            <Route exact path='/app/pmo-charge/card/:id' component={PMOChargeCard} />
            <Route exact path='/app/pmo-charge/calendar' component={PMOChargeCalendarView} />

            <Route exact path='/app/sp-leave' render={() => <Redirect to='/app/sp-leave/list' />} />
            <Route exact path='/app/sp-leave/list' component={SPLeaveList} />
            <Route exact path='/app/sp-leave/card' component={SPLeaveCard} />
            <Route exact path='/app/sp-leave/card/:id' component={SPLeaveCard} />

            <Route exact path='/app/material-return' render={() => <Redirect to='/app/material-return/list' />} />
            <Route exact path='/app/material-return/list' component={MaterialReturnList} />
            <Route exact path='/app/material-return/card' component={MaterialReturnCard} />
            <Route exact path='/app/material-return/card/:id' component={MaterialReturnCard} />

            <Route exact path='/app/loyalty-voucher/profile' component={LoyaltyVoucherProfileListPage} />
            <Route exact path='/app/loyalty-voucher/profile/new' component={LoyaltyVoucherProfilePage} />
            <Route exact path='/app/loyalty-voucher/profile/:profileId' component={LoyaltyVoucherProfilePage} />

            <Route exact path='/app/equipment' render={() => <Redirect to='/app/equipment/list' />} />
            <Route exact path='/app/equipment/list' component={EquipmentList} />
            <Route exact path='/app/equipment/card' component={EquipmentCard} />
            <Route exact path='/app/equipment/card/:id' component={EquipmentCard} />

            <Route exact path='/app/loan-equipment' render={() => <Redirect to='/app/loan-equipment/list' />} />
            <Route exact path='/app/loan-equipment/list' component={LoanEquipmentList} />
            <Route exact path='/app/loan-equipment/card' component={LoanEquipmentCard} />
            <Route exact path='/app/loan-equipment/card/:id' component={LoanEquipmentCard} />

            <Route exact path='/app/pmo-charge-worker' render={() => <Redirect to='/app/pmo-charge-worker/list' />} />
            <Route exact path='/app/pmo-charge-worker/list' component={PMOChargeWorkerList} />
            <Route exact path='/app/pmo-charge-worker/card' component={PMOChargeWorkerCard} />
            <Route exact path='/app/pmo-charge-worker/card/:id' component={PMOChargeWorkerCard} />
            <Route exact path='/app/loyalty-voucher/profile' component={CustomerVoucherListPage} />
            <Route exact path='/app/pmo-charge-worker/import-rate' component={PMOChargeWorkerImportRate} />
            <Route exact path='/app/pmo-charge-worker/worker-config/list' component={PMOChargeWorkerConfigListPage} />
            <Route exact path='/app/pmo-charge-worker/worker-config/new' component={PMOChargeWorkerConfigCardPage} />
            <Route exact path='/app/pmo-charge-worker/worker-config/:username'
                   component={PMOChargeWorkerConfigCardPage} />

            <Route exact path='/app/permit-to-work' render={() => <Redirect to='/app/permit-to-work/list' />} />
            <Route exact path='/app/permit-to-work/list' component={PermitToWorkList} />
            <Route exact path='/app/permit-to-work/card' component={PermitToWorkCard} />
            <Route exact path='/app/permit-to-work/card/:id' component={PermitToWorkCard} />

          </Switch>
        </div>
        <Rightbar />
      </>
    </div>
  );
}

export default withRouter(Layout);
